import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import LoaderBackground from "assets/images/LoaderBackground.svg?react";
import Loader from "assets/images/Loader.svg?react";
import ErrorIcon from "assets/images/ErrorIcon.svg?react";
import useLeumitTokenValidation from "hooks/useLeumitTokenValidation.hook";
import { styles } from "./LeumitTokenGate.styles";

type LeumitTokenGateProps = {
  children: ReactNode;
};

const useStyles = makeStyles()((theme) => styles(theme));

const LeumitTokenGate: FC<LeumitTokenGateProps> = ({ children }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { status, retry } = useLeumitTokenValidation();

  if (status === "valid") {
    return <>{children}</>;
  }

  const isRetryableError = status === "error";
  const isInvalid = status === "invalid";
  const isLoading = status === "idle" || status === "validating";

  const titleKey = isRetryableError
    ? "common:LeumitTokenValidation:errorTitle"
    : "common:LeumitTokenValidation:invalidTitle";
  const descriptionKey = isRetryableError
    ? "common:LeumitTokenValidation:errorDescription"
    : "common:LeumitTokenValidation:invalidDescription";

  return (
    <DashboardLayout>
      <MDBox className={classes.container}>
        {isLoading ? (
          <MDBox
            className={classes.loaderSection}
            data-test-id="leumit-token-validating"
          >
            <div className={classes.loader}>
              <LoaderBackground />
              <Loader className={classes.loaderBody} />
            </div>
            <MDTypography variant="body1">
              {t("common:LeumitTokenValidation:validating")}
            </MDTypography>
          </MDBox>
        ) : (
          <MDBox
            role="alert"
            data-test-id={isInvalid ? "leumit-token-invalid" : "leumit-token-error"}
          >
            <ErrorIcon className={classes.icon} />
            <MDTypography variant="h4" className={classes.title}>
              {t(titleKey)}
            </MDTypography>
            <MDTypography variant="body1" className={classes.description}>
              {t(descriptionKey)}
            </MDTypography>
            {isRetryableError ? (
              <MDButton
                variant="contained"
                color="primary"
                className={classes.retryButton}
                onClick={retry}
                data-test-id="leumit-token-retry"
                aria-label={t("common:LeumitTokenValidation:retry")}
              >
                {t("common:LeumitTokenValidation:retry")}
              </MDButton>
            ) : null}
          </MDBox>
        )}
      </MDBox>
    </DashboardLayout>
  );
};

export default LeumitTokenGate;
